import type { StateCreator } from "zustand";
import { create } from "zustand";
import { type DevtoolsOptions, devtools } from "zustand/middleware";
import type {
  NewsFeedFilterCategory,
  NewsFeedSidebarFilterItem,
} from "@/data-access/news-feeds/queries/news-feed-sidebar-filters-queries";
import {
  type ArticleTagManagementSlice,
  articleTagManagementInitialState,
  createArticleTagManagementActions,
} from "./article-tag-management/article-tag-management.slice";
import {
  createDynamicFiltersSlice,
  type DynamicFiltersSlice,
} from "./dynamic-filters.slice";
import { createNarrativeClusterManagementActions } from "./narrative-cluster-management/narrative-cluster-management.actions";
import {
  type NarrativeClusterManagementSlice,
  narrativeClusterManagementInitialState,
} from "./narrative-cluster-management/narrative-cluster-management.slice";
import { createNewsFeedFiltersActions } from "./newsFeed-filter.actions";

export type FilterCategoryData = {
  isLoading: boolean;
  data: NewsFeedSidebarFilterItem[];
  error: string | null;
};

export type NewsFeedFiltersSlice = {
  filters: Map<NewsFeedFilterCategory, FilterCategoryData>;
  expandedCategories: Set<NewsFeedFilterCategory>;
  clearAllFilters: () => void;
  setFilterLoading: (
    category: NewsFeedFilterCategory,
    isLoading: boolean,
  ) => void;
  setFilterError: (
    category: NewsFeedFilterCategory,
    error: string | null,
  ) => void;
  updateFilterData: (
    category: NewsFeedFilterCategory,
    data: NewsFeedSidebarFilterItem[],
  ) => void;
  upsertFilterCategory: (
    category: NewsFeedFilterCategory,
    next: { isLoading: boolean; data?: NewsFeedSidebarFilterItem[] },
  ) => void;
  setFilterCategory: (
    category: NewsFeedFilterCategory,
    data: NewsFeedSidebarFilterItem[],
    isLoading?: boolean,
  ) => void;
  removeFilterCategory: (category: NewsFeedFilterCategory) => void;
  setCategoryExpansion: (
    category: NewsFeedFilterCategory,
    expanded: boolean,
  ) => void;
} & DynamicFiltersSlice &
  NarrativeClusterManagementSlice &
  ArticleTagManagementSlice;

const createNewsFeedFilterSlice: StateCreator<
  NewsFeedFiltersSlice,
  [["zustand/devtools", never]]
> = (set, get, api) => ({
  filters: new Map([]),
  expandedCategories: new Set(["KEY_MESSAGE", "NARRATIVE_CLUSTER"]),
  ...articleTagManagementInitialState,
  ...narrativeClusterManagementInitialState,
  ...createNewsFeedFiltersActions(set, get, api),
  ...createDynamicFiltersSlice(set, get, api),
  ...createNarrativeClusterManagementActions(set, get, api),
  ...createArticleTagManagementActions(set, get, api),
});

const useNewsFeedFilters = create<NewsFeedFiltersSlice>()(
  devtools(createNewsFeedFilterSlice, {
    name: "news-feed-filter-store",
    serialize: {
      options: true,
    },
  } as unknown as DevtoolsOptions),
);

export default useNewsFeedFilters;
